import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { BaseRequest, ChangesSource, OpenShiftRequest } from "../../../utils/models";
import { hasNurseResponse } from "../../../utils/utilFunctions";

/**
 * Shared "merge two open-shift requests" helper. Used by `useUpdateRequestTime` (time edit) and
 * `useUpdateRequestFlatBonus` (bonus edit) — both detect an overlap, prompt the user, and on
 * Merge fold one request into the other.
 *
 * Picks the survivor based on pending responses (the side with responses stays), calls
 * `/staffing/merge-requests` so pickups transfer to the survivor, drops the source from local
 * state, and refreshes. Throws on API failure — callers handle their own error UX.
 */
export const useMergeRequests = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { setStaffRequests, refreshRequests } = useRequestsContext();

	const mergeRequests = async ({
		request,
		matching,
		newNeededNurses,
		source,
		successMessageKey,
	}: {
		request: BaseRequest;
		matching: OpenShiftRequest;
		newNeededNurses: number;
		source: ChangesSource;
		successMessageKey: string;
	}): Promise<{ sourceRequestId: string; targetRequestId: string }> => {
		const requestHasResponses = hasNurseResponse(request);
		const existingHasResponses = hasNurseResponse(matching);
		// Keep whichever side has responses; if neither does, keep the existing one.
		const [sourceRequestId, targetRequestId] = requestHasResponses && !existingHasResponses ? [matching.id, request.id] : [request.id, matching.id];

		await backendService.mergeRequests({
			source_request_id: sourceRequestId,
			target_request_id: targetRequestId,
			department_id: chosenDepartment.department_id,
			changed_request_has_responses: requestHasResponses,
			needed_nurses: newNeededNurses,
			source,
		});

		setStaffRequests((prev) => prev.filter((r) => r.id !== sourceRequestId));
		showSnackbar(t(successMessageKey), "success");
		await refreshRequests();
		return { sourceRequestId, targetRequestId };
	};

	return { mergeRequests };
};
